/**
 * PixiApp.jsx — PhaserGame.jsx 대체
 *
 * PixiJS 기반 게임 엔진을 React에 마운트합니다.
 * 기존 PhaserGame.jsx의 모든 기능을 PixiJS로 구현:
 * - 싱글턴 엔진 관리
 * - Scene 전환 (BattleScene, LobbyScene)
 * - 배틀 데이터 포워딩
 * - 로그 패널
 * - WebGL 컨텍스트 복구
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { t } from '../utils/i18n';
import { useStore } from '../store/useStore';
import { PixiEngine } from './pixi/PixiEngine';

let _singletonEngine = null;
let _createdOnce = false;
let _mountCount = 0;

export default function PixiApp() {
    const engineRef = useRef(null);
    const containerRef = useRef(null);
    const logsEndRef = useRef(null);
    const battle = useStore(state => state.battle);
    const coopBattle = useStore(state => state.coopBattle);
    const location = useLocation();

    const [logs, setLogs] = useState([]);
    const [showLog, setShowLog] = useState(false);
    const [isLoaded, setIsLoaded] = useState(false);
    const [contextLost, setContextLost] = useState(false);

    const isOnBattle = location.pathname === '/battle';
    const isOnHub = location.pathname === '/';
    const activeOverlay = useStore(s => s.activeOverlay);

    // Auto-scroll logs
    useEffect(() => {
        if (showLog && logsEndRef.current) {
            logsEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [logs, showLog]);

    // ── Main init effect ──
    useEffect(() => {
        _mountCount++;
        const thisMountId = _mountCount;

        // Reuse existing singleton
        if (_singletonEngine?.isRunning) {
            engineRef.current = _singletonEngine;
            setIsLoaded(true);
            return;
        }

        if (!containerRef.current) return;

        let engineInstance = null;

        const initEngine = async () => {
            try {
                // Lazy-import scenes so they use SceneCompat
                const [{ default: BattleScene }, { default: LobbyScene }] = await Promise.all([
                    import('./BattleScene'),
                    import('./LobbyScene'),
                ]);

                if (!containerRef.current) return;

                if (_singletonEngine?.isRunning) {
                    engineRef.current = _singletonEngine;
                    setIsLoaded(true);
                    return;
                }

                const BASE_W = 1280;
                const BASE_H = 720;

                const engine = new PixiEngine();
                await engine.init({
                    parent: containerRef.current,
                    width: BASE_W,
                    height: BASE_H,
                    transparent: true,
                    resolution: 1,
                });

                engineRef.current = engine;
                engineInstance = engine;
                _singletonEngine = engine;
                _createdOnce = true;

                // Canvas styling
                if (engine.canvas) {
                    engine.canvas.style.zIndex = '1';
                }

                // Registry compat
                engine.registry.set('store', useStore);
                engine.registry.set('dpr', 1);

                if (coopBattle) {
                    engine.registry.set('coopMode', true);
                    engine.registry.set('coopData', coopBattle);
                }

                // Register scenes
                const battleScene = new BattleScene();
                const lobbyScene = new LobbyScene();

                engine.addScene('BattleScene', battleScene);
                engine.addScene('LobbyScene', lobbyScene);

                // Setup battle log listener
                battleScene.events.on('log', (text) => {
                    setLogs(prev => [...prev.slice(-99), text]);
                });

                // WebGL context lost detection
                if (engine.canvas) {
                    engine.canvas.addEventListener('webglcontextlost', (e) => {
                        e.preventDefault();
                        setContextLost(true);
                    });
                    engine.canvas.addEventListener('webglcontextrestored', () => {
                        setContextLost(false);
                    });
                }

                console.info('[PixiApp] ✅ PixiJS engine created');
                setIsLoaded(true);

            } catch (error) {
                console.error('Failed to load game modules:', error);
            }
        };

        initEngine();

        // Cleanup
        return () => {
            const mountIdAtCleanup = thisMountId;
            const timer = setTimeout(() => {
                if (_mountCount > mountIdAtCleanup) return; // StrictMode re-mount
                if (_singletonEngine && _singletonEngine === engineInstance) {
                    console.info('[PixiApp] destroying engine');
                    engineInstance.destroy();
                    _singletonEngine = null;
                    _createdOnce = false;
                }
            }, 300);
        };
    }, []);

    // ── Forward battle data ──
    useEffect(() => {
        if (!engineRef.current || !battle) return;
        const scene = engineRef.current.scenes.get('BattleScene');
        if (scene?._isActive) {
            // Block forwarding during floor transitions — softReset hasn't run yet
            // so forwarding would create new sprites that softReset immediately destroys
            if (scene._isTransitioning) {
                console.info('[PixiApp] skipping updateBattle forward: scene is transitioning');
                return;
            }
            scene.events.emit('updateBattle', battle);
        }
    }, [battle]);

    // ── Scene switching: battle ──
    useEffect(() => {
        const engine = engineRef.current || _singletonEngine;
        if (!engine?.isRunning) return;

        const battleScene = engine.scenes.get('BattleScene');
        if (!battleScene) return;

        if (battle && isOnBattle) {
            if (!battleScene._isActive) {
                // Stop lobby first
                engine.scene.stop('LobbyScene');
                engine.scene.start('BattleScene');
                console.info('[PixiApp] scene → BattleScene');
            }
            if (battleScene._isActive) {
                battleScene.events.emit('updateBattle', battle);
            }
        } else {
            if (battleScene._isActive) {
                engine.scene.stop('BattleScene');
                console.info('[PixiApp] stopped BattleScene');
            }
        }
    }, [battle, isOnBattle, isLoaded]);

    // ── Scene switching: lobby ──
    useEffect(() => {
        const engine = engineRef.current || _singletonEngine;
        if (!engine?.isRunning) return;

        const lobbyScene = engine.scenes.get('LobbyScene');
        if (!lobbyScene) return;

        if (isOnHub) {
            if (!lobbyScene._isActive) {
                engine.scene.start('LobbyScene');
                console.info('[PixiApp] scene → LobbyScene');
            }
        } else {
            if (lobbyScene._isActive) {
                engine.scene.stop('LobbyScene');
            }
        }
    }, [isOnHub, isLoaded]);

    return (
        <div ref={containerRef} style={{
            width: '100%',
            height: '100%',
            overflow: 'hidden',
            touchAction: 'none',
            pointerEvents: (isOnBattle && !activeOverlay) ? 'auto' : 'none',
            background: 'transparent',
            position: 'fixed',
            top: 0,
            left: 0,
            zIndex: 0,
            visibility: 'visible',
        }}>
            {!isLoaded && (
                <div style={{
                    position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: '#9ca3af', fontSize: '14px', flexDirection: 'column', gap: '8px'
                }}>
                    <div className="spinner"></div>
                    <div>Loading Game Engine...</div>
                </div>
            )}

            {contextLost && (
                <div style={{
                    position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexDirection: 'column', gap: '16px',
                    background: 'rgba(0,0,0,0.85)', zIndex: 9999,
                }}>
                    <div style={{ color: '#f87171', fontSize: '20px', fontWeight: 700 }}>{t("ui.graphicsError")}</div>
                    <div style={{ color: '#d1d5db', fontSize: '14px', textAlign: 'center', maxWidth: '280px', lineHeight: 1.5 }}>
                        {t("ui.gpuLost")}
                    </div>
                    <button
                        onClick={() => window.location.reload()}
                        style={{
                            padding: '10px 28px', fontSize: '14px', fontWeight: 600,
                            background: '#3b82f6', color: '#fff', border: 'none',
                            borderRadius: '8px', cursor: 'pointer',
                        }}
                    >
                        {t('ui.reload')}
                    </button>
                </div>
            )}

            {/* Log Toggle */}
            <div
                onClick={() => setShowLog(!showLog)}
                style={{
                    position: 'absolute', top: '16px', right: '16px',
                    width: '36px', height: '36px',
                    background: 'rgba(31, 41, 55, 0.8)',
                    border: '2px solid #6b7280',
                    borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', zIndex: 100,
                    opacity: isLoaded ? 1 : 0, transition: 'opacity 0.3s'
                }}
            >
                <span style={{ fontSize: '16px' }}>📋</span>
            </div>

            {/* Log Panel */}
            {showLog && (
                <div style={{
                    position: 'absolute', top: '50%', left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 'min(85%, 350px)', height: '40%',
                    background: 'rgba(17, 24, 39, 0.95)',
                    border: '2px solid #374151',
                    borderRadius: '12px',
                    padding: '16px',
                    display: 'flex', flexDirection: 'column',
                    zIndex: 200
                }}>
                    <div style={{
                        textAlign: 'center', fontWeight: 'bold', color: '#e5e7eb', marginBottom: '8px', fontSize: '14px'
                    }}>
                        ⚔️ Battle Log
                    </div>
                    <div style={{
                        flex: 1, overflowY: 'auto', fontSize: '12px', color: '#9ca3af', lineHeight: '1.5'
                    }}>
                        {logs.map((log, i) => (
                            <div key={i} style={{ marginBottom: '4px' }}>{log}</div>
                        ))}
                        <div ref={logsEndRef} />
                    </div>
                </div>
            )}

            {showLog && (
                <div
                    onClick={() => setShowLog(false)}
                    style={{
                        position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                        zIndex: 150
                    }}
                />
            )}
        </div>
    );
}
